import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Copy, Download, FileText, Loader2, ShieldCheck, Landmark, CreditCard, Lock } from "lucide-react";
import { toast } from "sonner";
import { useT, useLanguage } from "@/i18n/LanguageContext";
import { CountrySelect } from "@/components/CountrySelect";
import { DateOfBirthSelect } from "@/components/DateOfBirthSelect";
import { getCountry } from "@/data/countries";
import { LEGAL_VERSIONS } from "@/data/legalVersions";

const API = process.env.REACT_APP_BACKEND_URL;

// ---------- Static labels ----------
const T = {
  stepper: [
    { en: "Personal details", sr: "Lični podaci" },
    { en: "Pre-agreement", sr: "Predugovor" },
    { en: "Payment", sr: "Plaćanje" },
    { en: "Confirmation", sr: "Potvrda" },
  ],
  back: { en: "Back to site", sr: "Nazad na sajt" },
  continue: { en: "Continue", sr: "Nastavi" },
  goBack: { en: "Back", sr: "Nazad" },
  submitting: { en: "Working…", sr: "Obrada…" },

  // STEP 1
  step1Title: { en: "Your details", sr: "Vaši podaci" },
  step1Desc: {
    en: "These details will be used in the pre-agreement and bank reference. They must match your ID document.",
    sr: "Ovi podaci se koriste u predugovoru i u poziv na broj kod banke. Moraju odgovarati vašem identifikacionom dokumentu.",
  },
  f: {
    fullName: { en: "Full legal name *", sr: "Puno zakonsko ime i prezime *" },
    dob: { en: "Date of birth *", sr: "Datum rođenja *" },
    passport: { en: "Passport / ID number *", sr: "Broj pasoša / lične karte *" },
    street: { en: "Street and number *", sr: "Ulica i broj *" },
    city: { en: "City *", sr: "Grad *" },
    postal: { en: "Postal code *", sr: "Poštanski broj *" },
    country: { en: "Country *", sr: "Država *" },
    email: { en: "Email *", sr: "Email *" },
    phone: { en: "Phone / WhatsApp *", sr: "Telefon / WhatsApp *" },
    dialCode: { en: "Code *", sr: "Pozivni *" },
  },
  validation: {
    age: {
      en: "You must be at least 18 years old to reserve.",
      sr: "Morate imati najmanje 18 godina za rezervaciju.",
    },
    email: {
      en: "Enter a valid email address (e.g. name@example.com).",
      sr: "Unesite ispravnu email adresu (npr. ime@primer.com).",
    },
    fillFields: {
      en: "Please complete all required fields marked with *.",
      sr: "Popunite sva obavezna polja označena sa *.",
    },
    willSaveAs: { en: "Will be saved as:", sr: "Sačuvaće se kao:" },
    forPassport: { en: "for", sr: "za" },
    draftRestored: {
      en: "Draft restored — clear",
      sr: "Skica vraćena — obriši",
    },
  },

  // STEP 2
  step2Title: { en: "Review your pre-agreement", sr: "Pregled predugovora" },
  step2Desc: {
    en: "Read carefully. The Serbian version legally prevails — toggle the language at any time. You may withdraw without giving a reason within 14 days of signing. Refunds are made within 30 days, subject to the conditions in Articles 6–7.",
    sr: "Pažljivo pročitajte. Srpska verzija je merodavna — možete promeniti jezik u svakom trenutku. Možete odustati bez navođenja razloga u roku od 14 dana od potpisivanja. Povraćaj se vrši u roku od 30 dana, pod uslovima iz čl. 6–7.",
  },
  typed: { en: "Type your full name to sign *", sr: "Upišite svoje puno ime za potpis *" },
  agreeText: {
    en: "I have read and agree to the terms of the $WINN Pre-Agreement.",
    sr: "Pročitao/la sam i prihvatam uslove $WINN Predugovora.",
  },
  consentIntro: {
    en: "Before we generate your draft pre-agreement, please confirm:",
    sr: "Pre nego što generišemo vaš nacrt predugovora, molimo potvrdite:",
  },
  consentPrivacyPre: { en: "I have read and accept the ", sr: "Pročitao/la sam i prihvatam " },
  consentPrivacyLink: { en: "Privacy Policy", sr: "Politiku privatnosti" },
  consentTermsPre: { en: "I have read and accept the ", sr: "Pročitao/la sam i prihvatam " },
  consentTermsLink: { en: "Terms & Conditions", sr: "Uslove korišćenja" },
  consentRequired: {
    en: "Please accept the Privacy Policy and the Terms & Conditions to continue.",
    sr: "Molimo prihvatite Politiku privatnosti i Uslove korišćenja da biste nastavili.",
  },
  agreeLegal: {
    en: "I have read and agree to the ",
    sr: "Pročitao/la sam i prihvatam ",
  },
  agreeLegalTerms: {
    en: "Terms & Conditions",
    sr: "Uslove korišćenja",
  },
  agreeLegalAnd: { en: " and ", sr: " i " },
  agreeLegalPrivacy: {
    en: "Privacy Policy",
    sr: "Politiku privatnosti",
  },
  agreeLegalPrivacySoon: {
    en: "Privacy Policy (coming soon)",
    sr: "Politiku privatnosti (uskoro)",
  },
  signCta: { en: "Sign electronically →", sr: "Elektronski potpiši →" },
  signedAt: { en: "Signed at", sr: "Potpisano" },

  // STEP 3
  step3Title: { en: "Pay by card", sr: "Plaćanje karticom" },
  step3Desc: {
    en: "Complete your reservation by paying securely with your card. You'll be taken to our payment provider Monri to enter your card details — Winfield never sees or stores your card number.",
    sr: "Završite rezervaciju sigurnim plaćanjem karticom. Bićete preusmereni na našeg provajdera plaćanja Monri da unesete podatke o kartici — Winfield nikada ne vidi niti čuva broj vaše kartice.",
  },
  payCard: {
    amountLabel: { en: "Amount due", sr: "Iznos za uplatu" },
    contractLabel: { en: "Contract", sr: "Ugovor" },
    choose: { en: "Choose how to pay", sr: "Izaberite način plaćanja" },
    full: { en: "Pay in full", sr: "Plati u celosti" },
    fullNote: { en: "One card payment", sr: "Jedno plaćanje karticom" },
    deposit: { en: "Pay deposit", sr: "Plati depozit" },
    depositNote: {
      en: "Reserve now, then 24 × €500 monthly",
      sr: "Rezervišite sada, zatim 24 × 500 € mesečno",
    },
    secure: {
      en: "Secure payment processed by Monri WebPay. Cards accepted: Visa, Mastercard, Maestro, Dina.",
      sr: "Sigurno plaćanje obrađuje Monri WebPay. Prihvaćene kartice: Visa, Mastercard, Maestro, Dina.",
    },
    payNow: { en: "Pay by card", sr: "Plati karticom" },
    methodCard: { en: "Pay by card", sr: "Karticom" },
    methodSepa: { en: "Bank transfer (SEPA)", sr: "Bankarski transfer (SEPA)" },
    sepaBtn: { en: "Get bank details & proforma", sr: "Dobij podatke za uplatu i predračun" },
    sepaNote: {
      en: "We'll show you the IBAN/SWIFT and a downloadable proforma. Your reservation is held while we wait for the transfer to arrive.",
      sr: "Prikazaćemo IBAN/SWIFT i predračun za preuzimanje. Rezervacija se drži dok čekamo da transfer stigne.",
    },
    redirecting: { en: "Redirecting to secure payment…", sr: "Preusmeravanje na sigurno plaćanje…" },
    cancelledMsg: {
      en: "Payment was cancelled. You can try again any time.",
      sr: "Plaćanje je otkazano. Možete pokušati ponovo u bilo kom trenutku.",
    },
  },

  // STEP 4
  step4Title: { en: "Reservation complete", sr: "Rezervacija završena" },
  step4TitleLater: { en: "Pre-agreement signed", sr: "Predugovor potpisan" },
  step4Body: {
    en: "Thank you. Your pre-agreement has been signed and stored. We've recorded your bank-transfer confirmation. Our team will reach out within 1–2 business days to confirm receipt and walk you through the next steps.",
    sr: "Hvala vam. Vaš predugovor je potpisan i sačuvan. Zabeleženo je da ste poslali bankarski transfer. Naš tim će vas kontaktirati u roku od 1–2 radna dana radi potvrde uplate i sledećih koraka.",
  },
  step4BodyLater: {
    en: "Your pre-agreement is signed and saved. To finalise the reservation, please send the EUR {deposit} deposit via SEPA bank transfer using the details below. The reservation is held for 7 days from signing.",
    sr: "Vaš predugovor je potpisan i sačuvan. Da biste finalizovali rezervaciju, pošaljite depozit od {deposit} EUR putem SEPA bankarskog transfera koristeći donje podatke. Rezervacija se drži 7 dana od potpisa.",
  },
  step4FieldLabels: {
    contract: { en: "Your contract number", sr: "Vaš broj ugovora" },
    status: { en: "Status", sr: "Status" },
    nextSteps: { en: "What happens next", sr: "Šta sledi" },
    depositReminder: { en: "Send your deposit", sr: "Pošaljite depozit" },
    documentHeading: { en: "Your signed pre-agreement", sr: "Vaš potpisani predugovor" },
    documentSub: {
      en: "Save a copy for your records. We've also kept it on file under your contract number.",
      sr: "Sačuvajte kopiju za svoju evidenciju. Mi takođe čuvamo dokument pod vašim brojem ugovora.",
    },
    download: { en: "Download signed PDF", sr: "Preuzmite potpisani PDF" },
    contactHeading: { en: "Need help?", sr: "Potrebna vam je pomoć?" },
    contactBody: {
      en: "Reply to your confirmation email or write to us at hello@winfieldtoken.com — quote your contract number and we'll respond within one business day.",
      sr: "Odgovorite na potvrdni email ili nam pišite na hello@winfieldtoken.com — navedite svoj broj ugovora i odgovorićemo u roku od jednog radnog dana.",
    },
    backToPay: { en: "Back to payment details", sr: "Nazad na podatke za uplatu" },
  },
  step4NextSteps: {
    en: [
      "We confirm receipt of the deposit (1–2 business days).",
      "Monthly instalments start the 1st of the following month.",
      "Your $WINN tokens are issued upon KHoV approval + occupancy permit.",
      "You can withdraw within 14 days of signing — 100% refund, no questions asked.",
    ],
    sr: [
      "Potvrđujemo prijem depozita (1–2 radna dana).",
      "Mesečne rate počinju 1. u sledećem mesecu.",
      "$WINN tokeni se izdaju po odobrenju KHoV i upotrebnoj dozvoli.",
      "Možete odustati u roku od 14 dana od potpisa — 100% povraćaj, bez pitanja.",
    ],
  },
  step4NextStepsLater: {
    en: [
      "Send the EUR {deposit} deposit via SEPA bank transfer using the details above.",
      "Use the payment reference exactly — this is how we match your transfer.",
      "Once we receive it (typically 1–2 business days), you'll get a confirmation email.",
      "Monthly instalments start the 1st of the month following confirmation.",
      "You can withdraw within 14 days of signing — 100% refund, no questions asked.",
    ],
    sr: [
      "Pošaljite depozit od {deposit} EUR putem SEPA bankarskog transfera koristeći gornje podatke.",
      "Koristite poziv na broj tačno — tako povezujemo vaš transfer.",
      "Kada primimo uplatu (obično 1–2 radna dana), dobićete potvrdni email.",
      "Mesečne rate počinju 1. u mesecu nakon potvrde.",
      "Možete odustati u roku od 14 dana od potpisa — 100% povraćaj, bez pitanja.",
    ],
  },
  returnHome: { en: "Back to homepage", sr: "Nazad na početnu" },
};

// ---------- Stepper ----------
const Stepper = ({ step }) => {
  const t = useT();
  return (
    <ol data-testid="reserve-stepper" className="flex items-center w-full gap-2 sm:gap-4">
      {T.stepper.map((label, i) => {
        const idx = i + 1;
        const state = idx < step ? "done" : idx === step ? "current" : "todo";
        return (
          <li key={i} className={`flex items-center gap-2 sm:gap-3 min-w-0 ${state === "current" ? "flex-1" : ""}`}>
            <span
              data-testid={`stepper-${idx}`}
              className={[
                "shrink-0 inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs font-semibold border",
                state === "done" ? "bg-[#0A0A0F] text-white border-[#0A0A0F]" : "",
                state === "current" ? "bg-white text-[#0A0A0F] border-[#0A0A0F]" : "",
                state === "todo" ? "bg-white text-[#62646B] border-[#E8E7E2]" : "",
              ].join(" ")}
            >
              {state === "done" ? <Check size={14} /> : idx}
            </span>
            {/* Label: full on desktop, only the current step on mobile */}
            <span
              className={[
                "text-xs sm:text-sm font-medium truncate",
                state === "todo" ? "text-[#62646B]" : "text-[#0A0A0F]",
                state === "current" ? "" : "hidden sm:inline",
              ].join(" ")}
            >
              {t(label)}
            </span>
            {i < T.stepper.length - 1 && (
              <span className="hidden sm:block flex-1 h-px bg-[#E8E7E2] ml-2" />
            )}
          </li>
        );
      })}
    </ol>
  );
};

// ---------- Field ----------
const Field = ({ label, name, value, onChange, type = "text", testid, autoComplete, error, hint, fieldRef, onBlur, ...rest }) => (
  <div>
    <label className="text-xs text-[#62646B] block mb-1.5">{label}</label>
    <input
      ref={fieldRef}
      data-testid={testid}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      onBlur={(e) => {
        // Catch browser autofill: when the field is autofilled, onChange may
        // not fire — but on blur the input has the populated value. Pipe it
        // through onChange so React state stays in sync.
        if (e.target.value !== value) {
          onChange?.({ target: { name, value: e.target.value } });
        }
        onBlur?.(e);
      }}
      autoComplete={autoComplete}
      aria-invalid={error ? "true" : undefined}
      className={`w-full px-4 py-3 rounded-xl bg-white border focus:outline-none transition-colors text-[#0A0A0F] ${
        error
          ? "border-red-400 focus:border-red-500"
          : "border-[#E8E7E2] focus:border-[#0A0A0F]"
      }`}
      {...rest}
    />
    {error && (
      <p className="text-[11px] text-red-600 mt-1.5" data-testid={testid ? `${testid}-error` : undefined}>
        {error}
      </p>
    )}
    {!error && hint && (
      <p className="text-[10px] text-[#9B9DA4] mt-1.5">{hint}</p>
    )}
  </div>
);

// Draft persistence key — non-sensitive fields only (excludes passport).
const DRAFT_KEY = "winfield_reserve_draft_v1";
// Maximum DOB year for age-18 guard. Computed once at module load.
const MAX_18_YEAR = new Date().getFullYear() - 18;
const EMPTY_FORM = {
  full_name: "",
  dob: { day: "", month: "", year: "" },
  passport_id_number: "",
  street: "",
  city: "",
  postal_code: "",
  address_country: "",   // ISO-2 country code
  email: "",
  phone_country: "",     // ISO-2 country code for dial prefix
  phone_local: "",       // local subscriber number
};

const readDraftSync = () => {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || typeof saved !== "object") return null;
    return {
      ...EMPTY_FORM,
      full_name: saved.full_name || "",
      dob: saved.dob && typeof saved.dob === "object"
        ? { day: saved.dob.day || "", month: saved.dob.month || "", year: saved.dob.year || "" }
        : EMPTY_FORM.dob,
      street: saved.street || "",
      city: saved.city || "",
      postal_code: saved.postal_code || "",
      address_country: saved.address_country || "",
      email: saved.email || "",
      phone_country: saved.phone_country || "",
      phone_local: saved.phone_local || "",
    };
  } catch {
    return null;
  }
};

// ============================================================
// Reserve page
// ============================================================
export default function Reserve() {
  const t = useT();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const inFlight = useRef(false);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [payOption, setPayOption] = useState("full");   // "full" | "deposit"
  const [payMethod, setPayMethod] = useState("card");   // "card" | "sepa"
  const [availability, setAvailability] = useState(null); // {capacity, taken, remaining, sold_out}
  const ALLOWED_UNITS = ["129", "141"];
  const UNIT_LABELS = {
    "129": { en: "Standard · 129", sr: "Standard · 129" },
    "141": { en: "Premium · 141", sr: "Premium · 141" },
  };
  const [unit, setUnit] = useState(() => {
    const u = new URLSearchParams(window.location.search).get("unit");
    return ALLOWED_UNITS.includes(u) ? u : "129";
  });

  // Lazy-init form + draft-restored flag from localStorage (no setState-in-effect).
  const [form, setForm] = useState(() => readDraftSync() || EMPTY_FORM);
  const [draftRestored, setDraftRestored] = useState(() => {
    const d = readDraftSync();
    return Boolean(d) && ["full_name","email","street","city","postal_code","phone_local"]
      .some((k) => d[k]);
  });
  const [errors, setErrors] = useState({});
  const [preAgreement, setPreAgreement] = useState(null);   // server doc
  const [paymentMode, setPaymentMode] = useState(null);     // "sent" | "later" | null
  const [typedName, setTypedName] = useState("");
  const [agree, setAgree] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Resume an existing reservation via ?id= (e.g. from the reservation link/banner
  // after our team has been in touch). Lands the buyer at the right step by status.
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get("id");
    if (!rid) return;
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/pre-agreements/${rid}`);
        if (!res.ok || !active) return;
        const pa = await res.json();
        if (!active) return;
        const st = pa.status;
        if (["deposit_paid", "paid", "tokens_issued", "awaiting_sepa", "payment_initiated"].includes(st)) {
          navigate(`/r/${pa.id}`);
          return;
        }
        setPreAgreement(pa);
        if (pa.package_snapshot?.apartment_unit) setUnit(String(pa.package_snapshot.apartment_unit));
        if (pa.buyer?.full_name) setForm((f) => ({ ...f, full_name: pa.buyer.full_name }));
        setStep(st === "signed" ? 3 : 2);
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch live unit availability (slots filled once a deposit/full payment lands).
  useEffect(() => {
    let active = true;
    setAvailability(null);
    fetch(`${API}/api/availability?unit=${unit}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setAvailability(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [unit]);

  // Lock the payment rail to the method confirmed on the verification call.
  const lockedPayMethod = preAgreement?.payment_method === "card" || preAgreement?.payment_method === "sepa"
    ? preAgreement.payment_method : null;
  useEffect(() => {
    if (lockedPayMethod) setPayMethod(lockedPayMethod);
  }, [lockedPayMethod]);


  const soldOut = availability?.sold_out === true;
  const fmtEurLang = (n) => Number(n).toLocaleString(lang === "sr" ? "de-DE" : "en-US");

  // Refs for scroll-to-first-error.
  const fieldRefs = useRef({});
  const setFieldRef = (name) => (el) => { fieldRefs.current[name] = el; };

  // Debounced draft saver — called whenever the form mutates (excl. passport).
  const saveTimer = useRef(null);
  const scheduleDraftSave = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        const copy = { ...next, passport_id_number: "" };
        window.localStorage.setItem(DRAFT_KEY, JSON.stringify(copy));
      } catch { /* quota or storage disabled — silently ignore */ }
    }, 400);
  };

  const clearDraft = () => {
    try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setForm(EMPTY_FORM);
    setErrors({});
    setDraftRestored(false);
  };

  const onField = (name) => (e) => {
    const v = e.target.value;
    setForm((p) => {
      const next = { ...p, [name]: v };
      scheduleDraftSave(next);
      return next;
    });
    if (errors[name]) setErrors((p) => ({ ...p, [name]: undefined }));
  };

  // Sanitize phone-local: strip a leading "+" or the selected country's dial
  // digits, since the dial code already comes from the country picker.
  const onPhoneLocal = (e) => {
    let raw = e.target.value;
    if (raw.length > 20) raw = raw.slice(0, 20);
    let v = raw.trimStart();
    if (v.startsWith("+")) v = v.slice(1).trimStart();
    const c = getCountry(form.phone_country);
    if (c) {
      const digits = c.dial.replace("+", "");           // e.g. "381", "1"
      const re = new RegExp(`^${digits}[\\s\\-()/]*`);
      v = v.replace(re, "");
    }
    setForm((p) => {
      const next = { ...p, phone_local: v };
      scheduleDraftSave(next);
      return next;
    });
    if (errors.phone_local) setErrors((p) => ({ ...p, phone_local: undefined }));
  };

  // Sanitize passport / ID: uppercase alphanumeric only (no spaces/symbols),
  // since passport & national-ID numbers worldwide are alphanumeric. Cap 15.
  const onPassport = (e) => {
    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
    setForm((p) => {
      const next = { ...p, passport_id_number: v };
      scheduleDraftSave(next);
      return next;
    });
    if (errors.passport_id_number) setErrors((p) => ({ ...p, passport_id_number: undefined }));
  };

  // Inline email format check (soft on blur).
  const validateEmail = () => {
    const v = form.email.trim();
    if (!v) return;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(v)) setErrors((p) => ({ ...p, email: t(T.validation.email) }));
  };

  // Derived: ISO date, full address string, full phone string
  const isoDob = useMemo(() => {
    const { day, month, year } = form.dob;
    if (!day || !month || !year) return "";
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }, [form.dob]);

  const composedAddress = useMemo(() => {
    const c = getCountry(form.address_country);
    const parts = [form.street, form.city, form.postal_code, c ? (lang === "sr" ? c.sr : c.en) : ""]
      .map((s) => (s || "").trim())
      .filter(Boolean);
    return parts.join(", ");
  }, [form.street, form.city, form.postal_code, form.address_country, lang]);

  const composedPhone = useMemo(() => {
    const c = getCountry(form.phone_country);
    if (!c) return form.phone_local.trim();
    return `${c.dial} ${form.phone_local.trim()}`.trim();
  }, [form.phone_country, form.phone_local]);

  // -------- Step 1 → create draft, advance to step 2 --------
  const submitStep1 = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;

    // 0) Safety-net: pull current DOM values from refs for any field React
    //    didn't catch via onChange (e.g., some browser autofill paths).
    const refSync = {};
    ["full_name","passport_id_number","street","city","postal_code","email","phone_local"].forEach((k) => {
      const el = fieldRefs.current[k];
      if (el && typeof el.value === "string" && el.value !== form[k]) {
        refSync[k] = el.value;
      }
    });
    const merged = { ...form, ...refSync };
    if (Object.keys(refSync).length) setForm((p) => ({ ...p, ...refSync }));

    // 1) Required-field map → which fields are empty?
    const nextErr = {};
    if (!merged.full_name.trim()) nextErr.full_name = t(T.validation.fillFields);
    if (!isoDob) nextErr.dob = t(T.validation.fillFields);
    if (!merged.passport_id_number.trim()) nextErr.passport_id_number = t(T.validation.fillFields);
    if (!merged.phone_country) nextErr.phone_country = t(T.validation.fillFields);
    if (!merged.phone_local.trim()) nextErr.phone_local = t(T.validation.fillFields);
    if (!merged.street.trim()) nextErr.street = t(T.validation.fillFields);
    if (!merged.city.trim()) nextErr.city = t(T.validation.fillFields);
    if (!merged.postal_code.trim()) nextErr.postal_code = t(T.validation.fillFields);
    if (!merged.address_country) nextErr.address_country = t(T.validation.fillFields);
    if (!merged.email.trim()) {
      nextErr.email = t(T.validation.fillFields);
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(merged.email.trim())) {
      nextErr.email = t(T.validation.email);
    }

    // 2) Age 18+ guard
    if (isoDob) {
      const [y, m, d] = isoDob.split("-").map(Number);
      const cutoff = new Date(y + 18, m - 1, d);
      if (cutoff > new Date()) nextErr.dob = t(T.validation.age);
    }

    // 3) Privacy + Terms consent (required before draft generation)
    if (!acceptPrivacy || !acceptTerms) nextErr.consent = t(T.consentRequired);

    if (Object.keys(nextErr).length) {
      setErrors(nextErr);
      const FIELD_ORDER = ["full_name","dob","passport_id_number","phone_country","phone_local","street","city","postal_code","address_country","email"];
      const firstKey = FIELD_ORDER.find((k) => nextErr[k]);
      const node = fieldRefs.current[firstKey];
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof node.focus === "function") setTimeout(() => node.focus(), 200);
      }
      toast.error(nextErr.consent && Object.keys(nextErr).length === 1 ? nextErr.consent : t(T.validation.fillFields));
      return;
    }

    const c = getCountry(merged.phone_country);
    const composedPhoneFinal = c
      ? `${c.dial} ${merged.phone_local.trim()}`.trim()
      : merged.phone_local.trim();
    const addrC = getCountry(merged.address_country);
    const composedAddressFinal = [merged.street, merged.city, merged.postal_code, addrC ? (lang === "sr" ? addrC.sr : addrC.en) : ""]
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join(", ");

    const buyerPayload = {
      full_name: merged.full_name.trim(),
      date_of_birth: isoDob,
      passport_id_number: merged.passport_id_number.trim(),
      address: composedAddressFinal,
      country: addrC ? (lang === "sr" ? addrC.sr : addrC.en) : "",
      email: merged.email.trim(),
      phone: composedPhoneFinal,
    };
    inFlight.current = true;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/pre-agreements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          buyer: buyerPayload,
          package_type: "type-a",
          apartment_unit: unit,
          language: lang,
          consents: {
            privacy_accepted: true,
            privacy_version: LEGAL_VERSIONS.privacy.version,
            terms_accepted: true,
            terms_version: LEGAL_VERSIONS.terms.version,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed");
      // Clear the persisted draft once we've successfully created the record.
      try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      setPreAgreement(data);
      saveResumeMarker(data);
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.message || "Network error");
    } finally {
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  // -------- Step 2 → sign --------
  const previewUrl = preAgreement
    ? `${API}/api/pre-agreements/${preAgreement.id}/preview?lang=${lang}&draft=true`
    : "";
  const previewPdfUrl = preAgreement
    ? `${API}/api/pre-agreements/${preAgreement.id}/preview.pdf?lang=${lang}`
    : "";

  const submitSign = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    if (!agree) {
      toast.error(lang === "sr" ? "Označite da prihvatate uslove." : "You must tick the agreement box.");
      return;
    }
    if (typedName.trim().toLowerCase() !== form.full_name.trim().toLowerCase()) {
      toast.error(lang === "sr" ? "Upišite tačno svoje ime kao u Koraku 1." : "Type your full name exactly as in Step 1.");
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/pre-agreements/${preAgreement.id}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typed_full_name: typedName, agree, language: lang }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setPreAgreement(data);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.message || "Network error");
    } finally {
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  // -------- Step 3 → start Monri card payment (redirect to hosted form) --------
  const startCardPayment = async () => {
    if (inFlight.current || !preAgreement) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      saveResumeMarker(preAgreement);
      const res = await fetch(`${API}/api/payments/monri/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pre_agreement_id: preAgreement.id, payment_option: payOption }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to start payment");
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.action;
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();   // navigates away to Monri — keep submitting state on
    } catch (err) {
      toast.error(err.message || "Network error");
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  // -------- Step 3 → bank transfer (SEPA): issue proforma, go to awaiting page --------
  const startSepa = async () => {
    if (inFlight.current || !preAgreement) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      saveResumeMarker(preAgreement);
      const res = await fetch(`${API}/api/payments/sepa/proforma`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pre_agreement_id: preAgreement.id, payment_option: payOption }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to create proforma");
      navigate(`/r/${preAgreement.id}?sepa=1`);
    } catch (err) {
      toast.error(err.message || "Network error");
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  // Persist the latest reservation id so the homepage can show a resume banner
  // (and so the public /r/:id page works after a reload).
  const saveResumeMarker = (doc) => {
    try {
      window.localStorage.setItem(
        "winfield_last_reservation",
        JSON.stringify({
          id: doc.id,
          contract_number: doc.contract_number,
          status: doc.status,
        }),
      );
    } catch { /* ignore */ }
  };

  return (
    <main className="min-h-screen bg-[#FAFAF7] text-[#0A0A0F]">
      <header className="border-b border-[#E8E7E2] bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="reserve-back-home" className="inline-flex items-center gap-2 text-sm font-medium text-[#62646B] hover:text-[#0A0A0F]">
            <ArrowLeft size={14} /> {t(T.back)}
          </Link>
          <div className="flex items-center gap-3">
            <img src="/img/winn-coin.png" alt="Winfield" className="w-8 h-8 object-contain" />
            <span className="font-heading font-semibold tracking-tight">Reserve · $WINN</span>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <Stepper step={step} />

        {/* ---------- STEP 1 ---------- */}
        {step === 1 && (
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            onSubmit={submitStep1}
            data-testid="step1-form"
            className="mt-10 bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10 wf-glow"
          >
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t(T.step1Title)}</h1>
            <p className="text-sm text-[#62646B] mt-2 max-w-2xl">{t(T.step1Desc)}</p>

            <div className="mt-6" data-testid="unit-selector">
              <p className="text-xs uppercase tracking-widest text-[#62646B] font-semibold mb-2">
                {lang === "sr" ? "Izaberite apartman" : "Select your apartment"}
              </p>
              <div className="flex flex-wrap gap-2">
                {ALLOWED_UNITS.map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setUnit(u)}
                    data-testid={`unit-option-${u}`}
                    aria-pressed={unit === u}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      unit === u
                        ? "bg-[#0A0A0F] text-white border-[#0A0A0F]"
                        : "bg-white text-[#0A0A0F] border-[#E8E7E2] hover:border-[#0A0A0F]"
                    }`}
                  >
                    {lang === "sr" ? UNIT_LABELS[u].sr : UNIT_LABELS[u].en}
                  </button>
                ))}
              </div>
            </div>

            {availability && (
              soldOut ? (
                <div
                  data-testid="sold-out-banner"
                  className="mt-6 px-5 py-4 rounded-2xl bg-[#FBEDED] border border-[#E7B7B7] text-[#7A1F1F]"
                >
                  <p className="font-semibold text-base">
                    {lang === "sr" ? "Jedinica je u potpunosti rezervisana" : "This unit is fully reserved"}
                  </p>
                  <p className="text-sm mt-1 text-[#8a3a3a]">
                    {lang === "sr"
                      ? `Svih ${availability.capacity} mesta za Apartman ${unit} je popunjeno. Izaberite drugu jedinicu ili nas kontaktirajte.`
                      : `All ${availability.capacity} slots for Apartment ${unit} are taken. Choose another unit or contact us.`}
                  </p>
                </div>
              ) : (
                <div
                  data-testid="slots-left-badge"
                  className="mt-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F1F7F1] border border-[#CDE3CD] text-[#2F6B33] text-xs font-semibold"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2F6B33]" />
                  {lang === "sr"
                    ? `Apartman ${unit} — još ${availability.remaining} od ${availability.capacity} mesta`
                    : `Apartment ${unit} — ${availability.remaining} of ${availability.capacity} slots left`}
                </div>
              )
            )}

            {draftRestored && (
              <div
                data-testid="draft-restored-banner"
                className="mt-5 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-[#FBF8F1] border border-[#E8E7E2] text-sm"
              >
                <span className="text-[#0A0A0F]">{t(T.validation.draftRestored)}</span>
                <button
                  type="button"
                  data-testid="clear-draft"
                  onClick={clearDraft}
                  className="text-[#62646B] hover:text-[#0A0A0F] underline underline-offset-2 text-xs font-medium"
                >
                  {lang === "sr" ? "obriši" : "clear"}
                </button>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              <Field
                label={t(T.f.fullName)}
                name="full_name"
                value={form.full_name}
                onChange={onField("full_name")}
                autoComplete="name"
                testid="input-full_name"
                error={errors.full_name}
                fieldRef={setFieldRef("full_name")}
              />
              <div ref={setFieldRef("dob")}>
                <label className="text-xs text-[#62646B] block mb-1.5">{t(T.f.dob)}</label>
                <DateOfBirthSelect
                  value={form.dob}
                  onChange={(next) => {
                    setForm((p) => {
                      const updated = { ...p, dob: next };
                      scheduleDraftSave(updated);
                      return updated;
                    });
                    if (errors.dob) setErrors((p) => ({ ...p, dob: undefined }));
                  }}
                  lang={lang}
                  testidPrefix="dob"
                  maxYear={MAX_18_YEAR}
                />
                {errors.dob && (
                  <p className="text-[11px] text-red-600 mt-1.5" data-testid="dob-error">{errors.dob}</p>
                )}
              </div>

              <Field
                label={
                  <>
                    {t(T.f.passport)}{" "}
                    {form.address_country && (
                      <span className="text-[#9B9DA4] font-normal">
                        — {t(T.validation.forPassport)}{" "}
                        <span className="text-base leading-none">{getCountry(form.address_country)?.flag}</span>{" "}
                        {lang === "sr" ? getCountry(form.address_country)?.sr : getCountry(form.address_country)?.en}
                      </span>
                    )}
                  </>
                }
                name="passport_id_number"
                value={form.passport_id_number}
                onChange={onPassport}
                testid="input-passport"
                error={errors.passport_id_number}
                fieldRef={setFieldRef("passport_id_number")}
              />
              <div className="grid grid-cols-[7.5rem_1fr] gap-2">
                <div ref={setFieldRef("phone_country")}>
                  <label className="text-xs text-[#62646B] block mb-1.5">{t(T.f.dialCode)}</label>
                  <CountrySelect
                    value={form.phone_country}
                    onChange={(code) => {
                      setForm((p) => {
                        const next = { ...p, phone_country: code };
                        scheduleDraftSave(next);
                        return next;
                      });
                      if (errors.phone_country) setErrors((p) => ({ ...p, phone_country: undefined }));
                    }}
                    variant="dial"
                    lang={lang}
                    testid="phone-country"
                  />
                  {errors.phone_country && (
                    <p className="text-[11px] text-red-600 mt-1.5">{errors.phone_country}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-[#62646B] block mb-1.5">{t(T.f.phone)}</label>
                  <input
                    ref={setFieldRef("phone_local")}
                    data-testid="input-phone"
                    name="phone_local"
                    type="tel"
                    inputMode="tel"
                    maxLength={20}
                    value={form.phone_local}
                    onChange={onPhoneLocal}
                    autoComplete="tel-national"
                    placeholder={lang === "sr" ? "lokalni broj" : "local number"}
                    aria-invalid={errors.phone_local ? "true" : undefined}
                    className={`w-full px-4 py-3 rounded-xl bg-white border focus:outline-none transition-colors text-[#0A0A0F] min-h-[46px] ${
                      errors.phone_local
                        ? "border-red-400 focus:border-red-500"
                        : "border-[#E8E7E2] focus:border-[#0A0A0F]"
                    }`}
                  />
                  {composedPhone && form.phone_country && form.phone_local.trim() && !errors.phone_local && (
                    <p className="text-[10px] text-[#62646B] mt-1.5" data-testid="phone-preview">
                      {t(T.validation.willSaveAs)} <span className="font-mono text-[#0A0A0F]">{composedPhone}</span>
                    </p>
                  )}
                  {errors.phone_local ? (
                    <p className="text-[11px] text-red-600 mt-1.5">{errors.phone_local}</p>
                  ) : (
                    <p className="text-[10px] text-[#9B9DA4] mt-1.5">
                      {lang === "sr"
                        ? "Ne unosite pozivni broj — već je izabran levo."
                        : "Don't type the country code — it's already selected on the left."}
                    </p>
                  )}
                </div>
              </div>

              <div className="sm:col-span-2">
                <Field
                  label={t(T.f.street)}
                  name="street"
                  value={form.street}
                  onChange={onField("street")}
                  autoComplete="street-address"
                  testid="input-street"
                  error={errors.street}
                  fieldRef={setFieldRef("street")}
                />
              </div>
              <Field
                label={t(T.f.city)}
                name="city"
                value={form.city}
                onChange={onField("city")}
                autoComplete="address-level2"
                testid="input-city"
                error={errors.city}
                fieldRef={setFieldRef("city")}
              />
              <Field
                label={t(T.f.postal)}
                name="postal_code"
                value={form.postal_code}
                onChange={onField("postal_code")}
                autoComplete="postal-code"
                testid="input-postal"
                error={errors.postal_code}
                fieldRef={setFieldRef("postal_code")}
              />
              <div className="sm:col-span-2" ref={setFieldRef("address_country")}>
                <label className="text-xs text-[#62646B] block mb-1.5">{t(T.f.country)}</label>
                <CountrySelect
                  value={form.address_country}
                  onChange={(code) => {
                    setForm((p) => {
                      const next = {
                        ...p,
                        address_country: code,
                        // Autofill the phone dial code when it hasn't been set yet.
                        phone_country: p.phone_country || code,
                      };
                      scheduleDraftSave(next);
                      return next;
                    });
                    if (errors.address_country) setErrors((p) => ({ ...p, address_country: undefined }));
                  }}
                  variant="country"
                  lang={lang}
                  testid="address-country"
                />
                {errors.address_country && (
                  <p className="text-[11px] text-red-600 mt-1.5">{errors.address_country}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <Field
                  label={t(T.f.email)}
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onField("email")}
                  onBlur={validateEmail}
                  autoComplete="email"
                  testid="input-email"
                  error={errors.email}
                  fieldRef={setFieldRef("email")}
                />
              </div>
            </div>

            <div className="mt-8 bg-[#FBF8F1] border border-[#E8D9B8] rounded-2xl p-5" data-testid="step1-consent">
              <p className="text-sm font-semibold text-[#0A0A0F]">{t(T.consentIntro)}</p>
              <label className="flex items-start gap-3 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="consent-privacy"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-[#0A0A0F]"
                />
                <span className="text-sm text-[#0A0A0F]">
                  {t(T.consentPrivacyPre)}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="consent-privacy-link" className="underline underline-offset-2 font-medium hover:text-[#62646B]">{t(T.consentPrivacyLink)}</Link>
                  <span className="text-[#9B9DA4]"> (v{LEGAL_VERSIONS.privacy.version} · {LEGAL_VERSIONS.privacy.date[lang] || LEGAL_VERSIONS.privacy.date.en})</span>
                </span>
              </label>
              <label className="flex items-start gap-3 mt-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="consent-terms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-[#0A0A0F]"
                />
                <span className="text-sm text-[#0A0A0F]">
                  {t(T.consentTermsPre)}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="consent-terms-link" className="underline underline-offset-2 font-medium hover:text-[#62646B]">{t(T.consentTermsLink)}</Link>
                  <span className="text-[#9B9DA4]"> (v{LEGAL_VERSIONS.terms.version} · {LEGAL_VERSIONS.terms.date[lang] || LEGAL_VERSIONS.terms.date.en})</span>
                </span>
              </label>
              {errors.consent && <p className="text-sm text-red-600 mt-2" data-testid="consent-error">{errors.consent}</p>}
            </div>

            <div className="flex justify-end mt-8">
              <button
                type="submit"
                disabled={submitting || soldOut}
                data-testid="step1-submit"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {soldOut ? (lang === "sr" ? "Rezervisano" : "Fully reserved") : submitting ? (<><Loader2 size={16} className="animate-spin" />{t(T.submitting)}</>) : (<>{t(T.continue)}<ArrowRight size={16} /></>)}
              </button>
            </div>
          </motion.form>
        )}

        {/* ---------- STEP 2 ---------- */}
        {step === 2 && preAgreement && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-10"
          >
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#62646B] font-semibold">{T.step4FieldLabels.contract[lang]}</p>
                <p className="font-heading text-xl font-semibold">{preAgreement.contract_number}</p>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-pdf-preview"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0A0A0F] underline underline-offset-2"
                >
                  <FileText size={14} /> {lang === "sr" ? "Otvori u novom prozoru" : "Open in new window"}
                </a>
                <a
                  href={previewPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-pdf-draft"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0A0A0F] underline underline-offset-2"
                >
                  <Download size={14} /> {lang === "sr" ? "Preuzmi PDF" : "Download PDF"}
                </a>
              </div>
            </div>

            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t(T.step2Title)}</h1>
            <p className="text-sm text-[#62646B] mt-2 max-w-2xl">{t(T.step2Desc)}</p>

            {(preAgreement.status === "draft" || preAgreement.status === "unreachable") && (
              <div data-testid="contact-pending-banner" className="mt-5 p-5 rounded-2xl bg-[#FBF8F1] border border-[#E8D9B8]">
                <p className="font-heading text-lg font-semibold flex items-center gap-2"><Lock size={17} className="text-[#8A6D1F]" />{lang === "sr" ? "Još jedan kratak korak pre potpisa" : "One quick step before you sign"}</p>
                <p className="text-sm text-[#62646B] mt-2 max-w-2xl">{lang === "sr" ? "Vaš nacrt predugovora je spreman za pregled ispod. Kontaktiraćemo Vas u roku od 24 sata radi potvrde podataka. Potpisivanje se otključava nakon što Vas kontaktiramo — bez putovanja. Vaša rezervacija je sačuvana; možete se vratiti bilo kada putem svog linka rezervacije." : "Your draft pre-agreement is ready to review below. We will contact you within 24 hours to confirm your details. Signing unlocks once we've been in touch — no travel required. Your reservation is saved; you can return any time using your reservation link."}</p>
              </div>
            )}
            {preAgreement.status === "declined" && (
              <div data-testid="contact-declined-banner" className="mt-5 p-5 rounded-2xl bg-[#FBEBEB] border border-[#EBCFCF]">
                <p className="font-heading text-lg font-semibold flex items-center gap-2 text-[#8a3a3a]"><Lock size={17} />{lang === "sr" ? "Ova rezervacija je zatvorena" : "This reservation is closed"}</p>
                <p className="text-sm text-[#8a3a3a] mt-2 max-w-2xl">{lang === "sr" ? "Ova rezervacija je označena kao odbijena. Ako je ovo greška ili želite da nastavite, kontaktirajte nas na hello@winfieldtoken.com." : "This reservation has been marked as declined. If this is a mistake or you'd like to proceed after all, please contact us at hello@winfieldtoken.com."}</p>
              </div>
            )}
            {preAgreement.status === "contacted" && (
              <div data-testid="contact-done-banner" className="mt-5 p-5 rounded-2xl bg-[#F1F7F1] border border-[#CDE3CD]">
                <p className="font-heading text-lg font-semibold flex items-center gap-2 text-[#2F6B33]"><ShieldCheck size={17} />{lang === "sr" ? "Potvrđeni ste — sada možete potpisati" : "You're confirmed — you can now sign"}</p>
                <p className="text-sm text-[#3f6b42] mt-2 max-w-2xl">{lang === "sr" ? "Hvala — naš tim je potvrdio vaše podatke. Pregledajte predugovor ispod i potpišite." : "Thanks — our team has confirmed your details. Please review the pre-agreement below and sign."}</p>
              </div>
            )}

            <div className="mt-6 bg-white border border-[#E8E7E2] rounded-3xl overflow-hidden">
              <iframe
                src={previewUrl}
                title="Pre-agreement preview"
                data-testid="pre-agreement-iframe"
                className="w-full"
                style={{ height: "62vh", minHeight: 500, border: 0 }}
              />
            </div>

            {preAgreement.status === "contacted" ? (
            <form
              onSubmit={submitSign}
              data-testid="step2-form"
              className="mt-6 bg-white border border-[#E8E7E2] rounded-3xl p-8 wf-glow"
            >
              <div>
                <label className="text-xs text-[#62646B] block mb-1.5">{t(T.typed)}</label>
                <input
                  data-testid="input-typed-name"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-[#FAFAF7] border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors font-heading text-2xl italic"
                  placeholder={form.full_name}
                />
              </div>

              <label className="flex items-start gap-3 mt-5 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="input-agree"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-1 w-4 h-4"
                />
                <span className="text-sm text-[#0A0A0F]">{t(T.agreeText)}</span>
              </label>

              <div className="flex items-center justify-between mt-8 gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  data-testid="step2-back"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E8E7E2] hover:border-[#0A0A0F] text-sm font-medium transition-colors"
                >
                  <ArrowLeft size={14} /> {t(T.goBack)}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="step2-sign"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors disabled:opacity-60"
                >
                  {submitting ? (<><Loader2 size={16} className="animate-spin" />{t(T.submitting)}</>) : (<><ShieldCheck size={16} />{t(T.signCta)}</>)}
                </button>
              </div>
            </form>
            ) : (
              <div data-testid="sign-locked-panel" className="mt-6 bg-white border border-[#E8E7E2] rounded-3xl p-8 flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#FBF8F1] border border-[#E8D9B8] flex items-center justify-center">
                  <Lock size={20} className="text-[#8A6D1F]" />
                </div>
                <p className="font-heading text-lg font-semibold">{lang === "sr" ? "Potpisivanje je zaključano" : "Signing is locked"}</p>
                <p className="text-sm text-[#62646B] max-w-md">{lang === "sr" ? "Otključava se čim vas naš tim kontaktira i potvrdi podatke. Sačuvajte svoj link rezervacije da biste se lako vratili." : "It unlocks as soon as our team contacts you to confirm your details. Save your reservation link so you can easily return."}</p>
                <div className="mt-2 w-full max-w-md bg-[#FAFAF7] border border-[#E8E7E2] rounded-xl px-3 py-2 flex items-center gap-2">
                  <span data-testid="locked-resume-url" className="text-xs text-[#62646B] font-mono truncate flex-1 min-w-0 text-left">{`${window.location.origin}/reserve?id=${preAgreement.id}`}</span>
                  <button
                    type="button"
                    data-testid="locked-copy-link"
                    onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/reserve?id=${preAgreement.id}`); toast.success(lang === "sr" ? "Link kopiran" : "Link copied"); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A0A0F] text-white text-xs font-semibold shrink-0"
                  >
                    <Copy size={12} /> {lang === "sr" ? "Kopiraj" : "Copy"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  data-testid="locked-back"
                  className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E8E7E2] hover:border-[#0A0A0F] text-sm font-medium transition-colors"
                >
                  <ArrowLeft size={14} /> {t(T.goBack)}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* ---------- STEP 3 ---------- */}
        {step === 3 && preAgreement && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-10"
          >
            <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10 wf-glow">
              <div className="flex items-center gap-3">
                <CreditCard size={22} className="text-[#0A0A0F]" />
                <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t(T.step3Title)}</h1>
              </div>
              <p className="text-sm text-[#62646B] mt-2 max-w-2xl">{t(T.step3Desc)}</p>

              {/* Payment method: card vs SEPA — locked to the method confirmed on the verification call */}
              {lockedPayMethod ? (
                <div data-testid="paymethod-locked" className="mt-7 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F1F7F1] border border-[#CDE3CD] text-sm font-medium text-[#2F6B33]">
                  {lockedPayMethod === "sepa" ? <Landmark size={14} /> : <CreditCard size={14} />}
                  {lockedPayMethod === "sepa" ? t(T.payCard.methodSepa) : t(T.payCard.methodCard)}
                  <Lock size={12} className="opacity-70" />
                </div>
              ) : (
                <div className="mt-7 inline-flex p-1 rounded-full bg-[#F0EFEA] border border-[#E8E7E2]">
                  {[
                    { key: "card", label: t(T.payCard.methodCard), icon: <CreditCard size={14} /> },
                    { key: "sepa", label: t(T.payCard.methodSepa), icon: <Landmark size={14} /> },
                  ].map((m) => (
                    <button
                      type="button"
                      key={m.key}
                      onClick={() => setPayMethod(m.key)}
                      data-testid={`paymethod-${m.key}`}
                      aria-pressed={payMethod === m.key}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${payMethod === m.key ? "bg-white text-[#0A0A0F] shadow-sm" : "text-[#62646B]"}`}
                    >
                      {m.icon} {m.label}
                    </button>
                  ))}
                </div>
              )}

              <p className="text-xs uppercase tracking-widest text-[#62646B] font-semibold mt-7">{t(T.payCard.choose)}</p>
              <div className="mt-3 grid sm:grid-cols-2 gap-4">
                {[
                  { key: "full", title: t(T.payCard.full), amount: Number(preAgreement.package_snapshot.package_price_eur ?? 14900), note: t(T.payCard.fullNote), testid: "payopt-full" },
                  { key: "deposit", title: t(T.payCard.deposit), amount: Number(preAgreement.package_snapshot.deposit_eur ?? 2900), note: `${lang === "sr" ? "Rezervišite sada, zatim" : "Reserve now, then"} ${preAgreement.package_snapshot.installment_count ?? 24} × €${Number(preAgreement.package_snapshot.monthly_instalment_eur ?? 500).toLocaleString(lang === "sr" ? "de-DE" : "en-US")} ${lang === "sr" ? "mesečno" : "monthly"}`, testid: "payopt-deposit" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => setPayOption(opt.key)}
                    data-testid={opt.testid}
                    aria-pressed={payOption === opt.key}
                    className={`text-left p-5 rounded-2xl border-2 transition-colors ${payOption === opt.key ? "border-[#0A0A0F] bg-[#FBF8F1]" : "border-[#E8E7E2] hover:border-[#9B9DA4]"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">{opt.title}</span>
                      <span className={`w-4 h-4 rounded-full border-2 ${payOption === opt.key ? "border-[#0A0A0F] bg-[#0A0A0F]" : "border-[#C9CDD6]"}`} />
                    </div>
                    <p className="font-heading text-2xl font-semibold mt-2">€{opt.amount.toLocaleString("de-DE")}</p>
                    <p className="text-xs text-[#62646B] mt-1">{opt.note}</p>
                  </button>
                ))}
              </div>

              <div className="mt-6 flex items-start gap-2.5 text-xs text-[#62646B] bg-white border border-[#E8E7E2] rounded-2xl p-4">
                <Lock size={15} className="text-[#0A0A0F] shrink-0 mt-0.5" />
                <span>{payMethod === "sepa" ? t(T.payCard.sepaNote) : t(T.payCard.secure)}</span>
              </div>

              <div className="mt-8">
                {payMethod === "sepa" ? (
                  <button
                    type="button"
                    onClick={startSepa}
                    disabled={submitting}
                    data-testid="step3-pay-sepa"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors disabled:opacity-60"
                  >
                    {submitting
                      ? (<><Loader2 size={16} className="animate-spin" />{t(T.submitting)}</>)
                      : (<><Landmark size={16} />{t(T.payCard.sepaBtn)}</>)}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startCardPayment}
                    disabled={submitting}
                    data-testid="step3-pay-card"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors disabled:opacity-60"
                  >
                    {submitting
                      ? (<><Loader2 size={16} className="animate-spin" />{t(T.payCard.redirecting)}</>)
                      : (<><CreditCard size={16} />{t(T.payCard.payNow)}</>)}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* ---------- STEP 4 ---------- */}
        {step === 4 && preAgreement && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="step4-confirmation"
            className="mt-10 space-y-6"
          >
            {/* Headline card */}
            <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10 wf-glow">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#0A0A0F] text-white">
                  <Check size={22} />
                </span>
                <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                  {paymentMode === "later" ? t(T.step4TitleLater) : t(T.step4Title)}
                </h1>
              </div>

              <div className="mt-7 grid sm:grid-cols-2 gap-4">
                <div className="p-5 rounded-2xl border border-[#E8E7E2]">
                  <p className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">{T.step4FieldLabels.contract[lang]}</p>
                  <p data-testid="confirm-contract-number" className="font-mono text-xl mt-2">{preAgreement.contract_number}</p>
                </div>
                <div className="p-5 rounded-2xl border border-[#E8E7E2]">
                  <p className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">{T.step4FieldLabels.status[lang]}</p>
                  <p className="text-base font-semibold mt-2 capitalize">{preAgreement.status.replace(/_/g, " ")}</p>
                </div>
              </div>

              <p className="text-sm text-[#62646B] leading-relaxed mt-6 max-w-2xl">
                {paymentMode === "later"
                  ? t(T.step4BodyLater).replace("{deposit}", fmtEurLang(preAgreement.package_snapshot.deposit_eur ?? 2900))
                  : t(T.step4Body)}
              </p>
            </div>

            {/* Bookmark / resumable link callout */}
            <div className="bg-[#FBF8F1] border border-[#E8E7E2] rounded-3xl p-6 lg:p-8" data-testid="step4-bookmark">
              <h2 className="font-heading text-base sm:text-lg font-semibold flex items-center gap-2">
                🔖 <span>{lang === "sr" ? "Sačuvajte ovu stranicu" : "Bookmark your reservation page"}</span>
              </h2>
              <p className="text-xs sm:text-sm text-[#62646B] leading-relaxed mt-2 max-w-2xl">
                {lang === "sr"
                  ? "Sačuvajte ovaj link kako biste se mogli vratiti u bilo koje vreme da pregledate ugovor, preuzmete PDF ili proverite uputstva za uplatu."
                  : "Save this link to return any time and view your contract, download the PDF, or check the payment instructions."}
              </p>
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <code data-testid="step4-resume-url" className="text-xs font-mono text-[#0A0A0F] bg-white border border-[#E8E7E2] rounded-lg px-3 py-2 break-all flex-1 min-w-0">
                  {`${window.location.origin}/r/${preAgreement.id}`}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    const url = `${window.location.origin}/r/${preAgreement.id}`;
                    navigator.clipboard.writeText(url).then(
                      () => toast.success(lang === "sr" ? "Kopirano!" : "Copied!"),
                      () => toast.error("Copy failed"),
                    );
                  }}
                  data-testid="step4-copy-link"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-xs sm:text-sm font-semibold transition-colors shrink-0"
                >
                  <Copy size={14} /> {lang === "sr" ? "Kopiraj" : "Copy link"}
                </button>
              </div>
              <p className="text-[10px] text-[#9B9DA4] mt-3">
                {lang === "sr"
                  ? "Izgubili ste link? Možete pretražiti rezervaciju po email-u i broju ugovora na "
                  : "Lost the link? You can look up your reservation by email + contract number at "}
                <Link to="/lookup" className="underline underline-offset-2 font-medium text-[#0A0A0F]">/lookup</Link>.
              </p>
            </div>

            {/* Signed document download */}
            <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10">
              <div className="flex items-center gap-3">
                <FileText size={20} className="text-[#0A0A0F]" />
                <h2 className="font-heading text-xl font-semibold tracking-tight">{T.step4FieldLabels.documentHeading[lang]}</h2>
              </div>
              <p className="text-sm text-[#62646B] leading-relaxed mt-2 max-w-2xl">{T.step4FieldLabels.documentSub[lang]}</p>
              <a
                href={`${API}/api/pre-agreements/${preAgreement.id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="step4-download-pdf"
                className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-sm font-semibold transition-colors"
              >
                <FileText size={15} /> {T.step4FieldLabels.download[lang]}
              </a>
            </div>

            {/* What's next */}
            <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10">
              <h3 className="font-heading text-lg font-semibold">{T.step4FieldLabels.nextSteps[lang]}</h3>
              <ul className="mt-3 space-y-2 text-sm text-[#0A0A0F]">
                {(paymentMode === "later" ? T.step4NextStepsLater : T.step4NextSteps)[lang].map((line, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0F] shrink-0 mt-2" />
                    <span>{line.replace("{deposit}", fmtEurLang(preAgreement.package_snapshot.deposit_eur ?? 2900))}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact + return */}
            <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10">
              <h3 className="font-heading text-lg font-semibold">{T.step4FieldLabels.contactHeading[lang]}</h3>
              <p className="text-sm text-[#62646B] leading-relaxed mt-2 max-w-2xl">{T.step4FieldLabels.contactBody[lang]}</p>
              <div className="mt-6">
                <Link
                  to="/"
                  data-testid="step4-home"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#E8E7E2] hover:border-[#0A0A0F] text-sm font-medium transition-colors"
                >
                  <ArrowLeft size={14} /> {t(T.returnHome)}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
